import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { company } from '~/company/Company';
import { EmptyWithFilters } from '~/components/EmptyWithFilters';
import ProductCard from '~/components/ProductCard/ProductCard';
import ProductCardSkeletonM from '~/components/ProductCardV3/skeletons/ProductCardSkeletonM';
import EmptyState from '~/pages/Catalog/EmptyState';
import { InfinityScrollLoader } from '~/pages/Catalog/InfinityScrollLoader';
import { orderStore } from '~/stores';
import type { CatalogCategory } from '~/stores/CategoriesStore';

import styles from './ProductsView.module.scss';

const ProductsView = ({
  className,
  category,
  handleResetFilters,
}: {
  className?: string;
  category: CatalogCategory;
  handleResetFilters: (options?: { mobile?: boolean }) => void;
}) => {
  const rootRef = useRef<Nullable<HTMLDivElement>>(null);
  const [searchParams] = useSearchParams();
  const showProducts = searchParams.get('showProducts') === 'true';

  if (!category.data) {
    return (
      <div className={styles.root}>
        <div className={styles.grid}>{getSkeletons(48)}</div>
      </div>
    );
  }

  if (category.isNotFound) {
    return (
      <div className={styles.notFound}>
        <EmptyState />
      </div>
    );
  }

  if (!category.products?.length) {
    if (showProducts && category.paginationRequest.isLoading) {
      return (
        <div className={styles.root}>
          <div className={styles.grid}>{getSkeletons(48)}</div>
        </div>
      );
    }

    return (
      <div className={styles.emptyCategory}>
        <EmptyWithFilters onReset={() => handleResetFilters()} />
      </div>
    );
  }

  return (
    <div className={classes([styles.root, className])} ref={rootRef}>
      {category.data && category.paginationRequest.isLoading && (
        <div className={styles.loader}>
          <span className="spinner" />
        </div>
      )}
      <div className={styles.grid}>
        {category.products.map((product) => (
          <ProductCard
            key={`${product.slug}_${product.id}_${product.productId}`}
            product={product}
            variant={company.productCardVariantL}
          />
        ))}
        {category.paginationRequest.canLoadMore && (
          <InfinityScrollLoader
            embedded
            className={styles.grid}
            onLoadMore={() => {
              category.paginationRequest.debounceLoadMore(
                orderStore.etaWarehouseCode,
              );
              category.paginationRequest.setStatus('fulfilled');
            }}
            skeletons={<>{getSkeletons(4)}</>}
          />
        )}
      </div>
    </div>
  );
};

const getSkeletons = (length: number) =>
  Array.from({ length }, () => uuid()).map((key) => (
    <ProductCardSkeletonM key={key} />
  ));

export default observer(ProductsView);
